/**
 * External Imports
 */
import { FC, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { NeuNavVertical } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { logListClick, ListFilters } from '../../utils/analyticsHelpers';
import { setLastPath } from '../../utils/helpers';

/**
 * Style Imports
 */
import { SideNavLabel } from './SideNav.styles';

interface ValidationSideNavProps {
  type: string;
  total: number;
  completedCount: number;
  toDoCount: number;
  expertCount: number;
  competentCount: number;
  developingCount: number;
}

const ValidationSideNav: FC<ValidationSideNavProps> = ({
  type,
  total,
  completedCount,
  toDoCount,
  expertCount,
  competentCount,
  developingCount
}) => {
  const { validationFilter = 'all' } = useParams();

  const { logTrackingEvent } = useAnalyticsApi();

  const validationFilters = useMemo(
    () => [
      { filter: 'All', count: total },
      { filter: 'Completed', count: completedCount },
      { filter: 'To Do', count: toDoCount },
      { filter: 'E - Expert', count: expertCount },
      { filter: 'C - Competent', count: competentCount },
      { filter: 'D - Developing', count: developingCount }
    ],
    [
      total,
      completedCount,
      toDoCount,
      expertCount,
      competentCount,
      developingCount
    ]
  );

  const validationSubPath = useMemo(() => `${type}/list`, [type]);
  const changeTab = useCallback(
    (filter: string) => {
      switch (filter) {
        case 'All':
        case 'Completed':
        case 'To Do':
        case 'E - Expert':
        case 'C - Competent':
        case 'D - Developing':
          logTrackingEvent(
            logListClick(
              filter
                ?.toLowerCase()
                .replace(/^([a-z]\s-\s)/, '')
                .replace(/ /g, '-') as ListFilters,
              `${type}/list`
            )
          );
          setLastPath(
            filter === 'All'
              ? `/validation/${validationSubPath}`
              : `/validation/${validationSubPath}/${filter
                  ?.toLowerCase()
                  .replace(/^([a-z]\s-\s)/, '')
                  .replace(/ /g, '-')}`
          );
          break;
        default:
          console.log('No event for tab filter');
      }
    },
    [logTrackingEvent, logListClick, type]
  );

  // to do: edit Links to show blue/bold when active
  return (
    <NeuNavVertical
      id="validation-side-nav"
      className="h-auto"
      kind="outlined"
      label="MENU"
      menu="blue"
      mode="drawer"
      style={{
        boxShadow: 'rgba(0, 0, 0, 0.15) 0 2px 4px',
        '--nav-width-drawer': 'clamp(200px, 25vw, 200px)'
      }}
    >
      <NeuNavVertical
        unit="header"
        style={{
          padding:
            '0 calc(var(--nav-padding-x) / 1.5) calc(var(--nav-padding-y) / 0.75)'
        }}
      />
      <NeuNavVertical unit="content" style={{ overflow: 'clip' }}>
        {validationFilters &&
          validationFilters.map(({ filter, count }) => (
            <Link
              key={filter}
              to={
                filter === 'All'
                  ? `/validation/${validationSubPath}`
                  : `/validation/${validationSubPath}/${filter
                      ?.toLowerCase()
                      .replace(/^([a-z]\s-\s)/, '')
                      .replace(/ /g, '-')}`
              }
              onClick={() => changeTab(filter)}
            >
              <NeuNavVertical
                id={`Side-Nav-Validation-${filter}-Link`}
                className="ps-2"
                active={
                  validationFilter ===
                  filter
                    ?.toLowerCase()
                    .replace(/^([a-z]\s-\s)/, '')
                    .replace(/ /g, '-')
                }
                unit="item"
              >
                <SideNavLabel
                  id={`Side-Nav-Validation-${filter}-Label`}
                  className="border border-dark"
                >
                  {filter} ({count})
                </SideNavLabel>
              </NeuNavVertical>
            </Link>
          ))}
      </NeuNavVertical>
    </NeuNavVertical>
  );
};
export default ValidationSideNav;
